import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import { Divider } from 'antd';
import { subject } from '@casl/ability';
import {
  AtsButtonMenu,
  Button,
  BUTTON_TYPES,
  Card,
  notification,
  NOTIFICATION_TYPES,
  RichText,
  Tooltip,
  User,
  Status,
  Priority,
} from '_atoms';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { PopupPauseOpening, PopupReopenOpening } from '_molecules';
import { getUser } from 'store/selectors/authorizeSelectors';
import { getJobData } from 'store/selectors/jobSelectors';
import history from 'helpers/history';
import { dateFormatter } from 'helpers/dateFormatter';
import { getOpeningAdditionalInfo } from 'actions';
import { BUTTON_TEXT } from 'constants/text';
import { OPENING_STATUS_TYPES, OPENING_STATUSES } from 'constants/opening';
import { ACTIONS, Can, ROLES, SUBJECTS, UI, useAbility } from 'permission';
import { ReactComponent as LinkIcon } from 'dist/icons/socialNetworks/Link.svg';
import { ReactComponent as Loader } from 'dist/icons/loader.svg';
import type { UserRole } from '_atoms/User';
import { useOpeningLabel } from './utils';
import type { OpeningProps, OpeningData } from './types';
import './style.scss';

export const Opening: React.FC<OpeningProps> = ({
  className,
  opening,
  removeOpening,
  closeOpening,
  pauseOpening,
  reopenOpening,
  approveDeclineOpening,
  editable,
  jobName,
  inResolution,
}) => {
  const ability = useAbility();
  const { id: jobId } = useParams<{ id: string }>();

  const user = useSelector(getUser);
  const jobData = useSelector(getJobData);

  const openingTL = opening.team_lead.uuid === user.uuid || jobData.team_lead?.uuid === user.uuid;

  const isLmApprover = user.roles?.find(
    (r: { name: string }) => r.name === ROLES.LOCATION_MANAGER
  )?.is_opening_approver;

  const [showMore, setShowMore] = useState(false);
  const [openConfirmPause, setOpenConfirmPause] = useState(false);
  const [openConfirmReopen, setOpenConfirmReopen] = useState(false);
  const [{ additionalInfo, additionalInfoLoading }, setAdditionalInfo] = useState<{
    additionalInfo: Record<string, unknown> | null;
    additionalInfoLoading: boolean;
  }>({ additionalInfo: null, additionalInfoLoading: false });

  const onShowMore = () => {
    setShowMore(s => !s);
    if (!additionalInfo && !additionalInfoLoading && opening.has_history) {
      setAdditionalInfo({ additionalInfo: null, additionalInfoLoading: true });
      getOpeningAdditionalInfo(jobId, opening.uuid)
        .then((resp: Record<string, unknown>) => {
          setAdditionalInfo({ additionalInfo: resp, additionalInfoLoading: false });
        })
        .catch(() => {
          setAdditionalInfo({ additionalInfo: null, additionalInfoLoading: false });
        });
    }
  };

  const showEdit = ability.can(ACTIONS.UPDATE, subject(SUBJECTS.OPENING, { status: opening.status })) && editable;
  const showClone = ability.can(ACTIONS.READ, UI.CLONE_OPENING) && editable;
  const showClose =
    showEdit && (opening.status === OPENING_STATUSES.OPEN || opening.status === OPENING_STATUSES.ON_HOLD);
  const showDelete = ability.can(ACTIONS.DELETE, subject(SUBJECTS.OPENING, { status: opening.status })) && editable;
  const showPause =
    ability.can(ACTIONS.READ, subject(UI.PAUSE_OPENING, { status: opening.status, openingTL })) && editable;
  const showReopen =
    ability.can(ACTIONS.READ, subject(UI.REOPEN_OPENING, { status: opening.status, openingTL })) && editable;

  const onApprove = () => approveDeclineOpening(opening, false);
  const onDecline = () => approveDeclineOpening(opening, true);
  const onEditOpening = () => history.push(`/jobs/${jobId}/create-opening/${opening.uuid}`);
  const onCloneOpening = () => history.push(`/jobs/${jobId}/clone-opening/${opening.uuid}`);

  const onCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(`${window.location.origin}/jobs/${jobId}/show/openings/?scroll=${opening.uuid}`)
        .then(() => notification({ message: 'Link to opening has been copied', type: NOTIFICATION_TYPES.SUCCESS }));
    }
  };

  const onOpenConfirmPause = () => setOpenConfirmPause(true);
  const onCloseConfirmPause = () => setOpenConfirmPause(false);
  const onOpenConfirmReopen = () => setOpenConfirmReopen(true);
  const onCloseConfirmReopen = () => setOpenConfirmReopen(false);
  const onPause = (data: unknown) => {
    onCloseConfirmPause();
    pauseOpening(opening.uuid, data);
  };
  const onReopen = (data: unknown) => {
    onCloseConfirmReopen();
    reopenOpening(opening.uuid, data);
  };

  const { label, label_desc, label_link } = useOpeningLabel(opening as Parameters<typeof useOpeningLabel>[0]);

  const workExperience =
    opening.work_experience === 'Custom' ? opening.work_experience_custom_value : opening.work_experience;

  return (
    <Card className={cn('opening-default', { resolution: inResolution }, className)}>
      <div className="opening-default__header">
        <div>
          <div className="opening-default__header__main-info">
            <Priority priority={opening.priority as 'Minor' | 'Medium' | 'Critical'} />
            <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>
              {`${opening.custom_level || opening.level} ${jobName}`}
            </Typography.Title2>
            <Status className="opening-default__status" type={OPENING_STATUS_TYPES[opening.status]}>
              {opening.status}
            </Status>
          </div>
          <Typography.Title3 className="opening-default__header__extra-info">
            {`Expected on ${dateFormatter(opening.expected)}`}
          </Typography.Title3>
        </div>
        <div>
          <div className="opening-default__buttons">
            <Can
              I={ACTIONS.READ}
              a={
                subject(UI.APPROVE_DECLINE, {
                  status: opening.status,
                  isApprover: isLmApprover,
                }) as any // eslint-disable-line @typescript-eslint/no-explicit-any
              }
            >
              <Button onClick={onDecline}>{BUTTON_TEXT.DECLINE}</Button>
              <Button type={BUTTON_TYPES.PRIMARY} onClick={onApprove}>
                {BUTTON_TEXT.APPROVE}
              </Button>
            </Can>
            {!inResolution && (
              <Tooltip label="Copy link" placement="top">
                <Button className="copy-link" icon={<LinkIcon />} type={BUTTON_TYPES.GHOST} onClick={onCopyLink} />
              </Tooltip>
            )}
            <AtsButtonMenu>
              {showEdit && (
                <li role="none" onClick={onEditOpening}>
                  Edit
                </li>
              )}
              {showClone && (
                <li role="none" onClick={onCloneOpening}>
                  Copy
                </li>
              )}
              {showClose && (
                <li role="none" onClick={() => closeOpening(opening)}>
                  Close
                </li>
              )}
              {showDelete && (
                <li role="none" className="active" onClick={() => removeOpening(opening)}>
                  Delete
                </li>
              )}
              {showPause && (
                <li role="none" className="active" onClick={onOpenConfirmPause}>
                  Pause
                </li>
              )}
              {showReopen && (
                <li role="none" className="active" onClick={onOpenConfirmReopen}>
                  Reopen
                </li>
              )}
            </AtsButtonMenu>
          </div>
        </div>
      </div>
      {!!label && !!label_desc && (
        <div className="opening-default__label">
          {label}
          <span>
            {label_link ? (
              <a href={label_link} target="_blank" rel="noreferrer noopener">
                {label_desc}
              </a>
            ) : (
              label_desc
            )}
          </span>
        </div>
      )}
      <div className="opening-default__body">
        <div className="opening-default__body__info">
          <div className="opening-default__body__main-info">
            <div className="col-1">
              <div className="info-block">
                <Typography.Label>Creation date</Typography.Label>
                <Typography.Text>{dateFormatter(opening.created || '')}</Typography.Text>
              </div>
              {opening.sh_approval_date && (
                <div className="info-block">
                  <Typography.Label>SH approval date</Typography.Label>
                  <Typography.Text>{dateFormatter(opening.sh_approval_date)}</Typography.Text>
                </div>
              )}
              {opening.rm_approval_date && (
                <div className="info-block">
                  <Typography.Label>RM approval date</Typography.Label>
                  <Typography.Text>{dateFormatter(opening.rm_approval_date)}</Typography.Text>
                </div>
              )}
              {opening.lm_approval_date && (
                <div className="info-block">
                  <Typography.Label>LM approval date</Typography.Label>
                  <Typography.Text>{dateFormatter(opening.lm_approval_date)}</Typography.Text>
                </div>
              )}
              {opening.hrl_approval_date && (
                <div className="info-block">
                  <Typography.Label>HRL approval date</Typography.Label>
                  <Typography.Text>{dateFormatter(opening.hrl_approval_date)}</Typography.Text>
                </div>
              )}
            </div>
            <div className="col-2">
              <div className="info-block">
                <Typography.Label>Office location</Typography.Label>
                <Typography.Text>
                  {opening.offices?.map((o: { name: string }) => o.name).join(', ') || ''}
                </Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Company</Typography.Label>
                <Typography.Text>{opening.company}</Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Foreign language</Typography.Label>
                <Typography.Text>
                  {opening.foreign_languages?.length
                    ? opening.foreign_languages.map((l: { language: string; level: string }) => (
                        <div key={l.language + l.level}>
                          {l.language}: {l.level}
                        </div>
                      ))
                    : '-'}
                </Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Employment type</Typography.Label>
                <Typography.Text>
                  {opening.employment}
                  {opening.employment === 'Hourly' && `, ${opening.employment_custom_value}`}
                </Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Required work experience</Typography.Label>
                <Typography.Text>
                  {`${workExperience} year${Number(workExperience) % 10 !== 1 ? 's' : ''}`}
                </Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Technology area</Typography.Label>
                <Typography.Text>
                  {opening.technology_area
                    ? `${opening.technology_area.name}${opening.technology_area.is_outdated ? ' (Outdated)' : ''}`
                    : '-'}
                </Typography.Text>
              </div>
              <div className="info-block">
                <Typography.Label>Affects utilization</Typography.Label>
                <Typography.Text>{opening.is_affects_utilization ? 'Yes' : 'No'}</Typography.Text>
              </div>
            </div>
            <div className="col-3">
              <div className="info-block">
                <User
                  avatar={opening?.team_lead?.avatar}
                  fullName={opening?.team_lead?.full_name}
                  roles={opening?.team_lead?.roles as UserRole[] | undefined}
                  className="opening-default__team-lead"
                />
              </div>
              <div className="info-block">
                <div className="opening-default__comment">
                  <Typography.Label className="opening-default__body__extra-info__label">Comment</Typography.Label>
                  <RichText text={opening.comment || '-'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={cn('opening-default__body__extra-info', { hidden: !showMore })}>
        <div className="info-block">
          <Typography.Label className="opening-default__body__extra-info__label">Responsibilities</Typography.Label>
          <RichText text={opening.technical_responsibilities || '-'} opened />
        </div>
        <div className="info-block">
          <Typography.Label className="opening-default__body__extra-info__label">Requirements</Typography.Label>
          <RichText text={opening.technical_requirements || '-'} opened />
        </div>
        <div className="info-block">
          <Typography.Label className="opening-default__body__extra-info__label">
            Additional requirements
          </Typography.Label>
          <RichText text={opening.additional_requirements || '-'} opened />
        </div>
        <div className="info-block">
          <Typography.Label className="opening-default__body__extra-info__label">
            What makes this position attractive
          </Typography.Label>
          <RichText text={opening.position_advantages || '-'} opened />
        </div>
        {!!opening.requirements_responsibilities && opening.requirements_responsibilities !== '<p></p>' && (
          <div className="info-block">
            <Typography.Label className="opening-default__body__extra-info__label">
              Requirements/Responsibilities
            </Typography.Label>
            <RichText text={opening.requirements_responsibilities} opened />
          </div>
        )}
        {additionalInfoLoading && (
          <>
            <Divider />
            <div className="opening-default__body__extra-info__footer">
              <Loader />
            </div>
          </>
        )}
        {(opening.closed || opening.decline_reason || opening.status === OPENING_STATUSES.WAITING_FOR_ENTRY) && (
          <>
            <Divider />
            <div className="opening-default__body__extra-info__footer">
              {opening.closed && (
                <div className="opening-default__body__extra-info__footer__persons">
                  <div className="info-block">
                    <Typography.Label>Closed by</Typography.Label>
                    {(opening.closed as { user?: unknown }).user ? (
                      <User
                        className="opening-default__extra-user"
                        avatar={(opening.closed as { user: { avatar?: string } }).user?.avatar}
                        fullName={(opening.closed as { user: { full_name?: string } }).user?.full_name}
                        roles={(opening.closed as { user: { roles?: UserRole[] } }).user?.roles}
                      />
                    ) : (
                      <Typography.Text>-</Typography.Text>
                    )}
                  </div>
                  <div className="info-block">
                    <Typography.Label>Closure date</Typography.Label>
                    <Typography.Text>{dateFormatter((opening.closed as { date?: string }).date || '')}</Typography.Text>
                  </div>
                  {(opening.closed as { comment?: string }).comment && (
                    <div className="info-block">
                      <Typography.Label>Closure comment</Typography.Label>
                      <Typography.Text>{(opening.closed as { comment: string }).comment}</Typography.Text>
                    </div>
                  )}
                  {opening.status === OPENING_STATUSES.HIRED && opening.candidate && (
                    <div className="info-block">
                      <Typography.Label>Hired candidate</Typography.Label>
                      <a href={`/candidates/${opening.candidate.uuid}/show`} target="_blank" rel="noreferrer noopener">
                        {opening.candidate.full_name_latin || opening.candidate.full_name_local}
                      </a>
                    </div>
                  )}
                </div>
              )}
              {opening.decline_reason && (
                <div className="info-block">
                  <Typography.Label>Decline reason</Typography.Label>
                  <Typography.Text>{opening.decline_reason}</Typography.Text>
                </div>
              )}
              {opening.status === OPENING_STATUSES.WAITING_FOR_ENTRY && opening.linked_data && (
                <>
                  {(opening.linked_data as { linked_by?: unknown }).linked_by && (
                    <div className="info-block">
                      <Typography.Label>Linked by</Typography.Label>
                      <User
                        className="opening-default__extra-user"
                        avatar={(opening.linked_data as { linked_by: { avatar?: string } }).linked_by?.avatar}
                        fullName={(opening.linked_data as { linked_by: { full_name?: string } }).linked_by?.full_name}
                        roles={(opening.linked_data as { linked_by: { roles?: UserRole[] } }).linked_by?.roles}
                      />
                    </div>
                  )}
                  {(opening.linked_data as { linked_at?: string }).linked_at && (
                    <div className="info-block">
                      <Typography.Label>Linked at</Typography.Label>
                      <Typography.Text>
                        {dateFormatter((opening.linked_data as { linked_at: string }).linked_at)}
                      </Typography.Text>
                    </div>
                  )}
                  <div className="info-block">
                    <Typography.Label>Linked candidate</Typography.Label>
                    <a href={`/candidates/${opening.candidate?.uuid}/show`} target="_blank" rel="noreferrer noopener">
                      {opening.candidate?.full_name_latin || opening.candidate?.full_name_local}
                    </a>
                  </div>
                </>
              )}
            </div>
          </>
        )}
        {additionalInfo?.paused_at && (
          <>
            <Divider />
            <div className="opening-default__body__extra-info__footer">
              <div className="opening-pause-info">
                {additionalInfo.paused_at && (
                  <div className="opening-pause-info__item">
                    <div className="info-block">
                      <Typography.Label>Last paused by</Typography.Label>
                      <User
                        className="opening-default__extra-user"
                        avatar={(additionalInfo as { paused_by: { avatar?: string } }).paused_by?.avatar}
                        fullName={(additionalInfo as { paused_by: { full_name?: string } }).paused_by?.full_name}
                        roles={(additionalInfo as { paused_by: { roles?: UserRole[] } }).paused_by?.roles}
                      />
                    </div>
                    <div className="info-block">
                      <Typography.Label>Last paused at</Typography.Label>
                      <Typography.Text>{dateFormatter(String(additionalInfo.paused_at))}</Typography.Text>
                    </div>
                    <div className="info-block">
                      <Typography.Label>Pause comment</Typography.Label>
                      <RichText text={String((additionalInfo as { pause_comment?: string }).pause_comment || '-')} />
                    </div>
                  </div>
                )}
                {additionalInfo.reopen_at && (
                  <div className="opening-pause-info__item">
                    <div className="info-block">
                      <Typography.Label>Last reopen requested by</Typography.Label>
                      <User
                        className="opening-default__extra-user"
                        avatar={(additionalInfo as { reopen_by: { avatar?: string } }).reopen_by?.avatar}
                        fullName={(additionalInfo as { reopen_by: { full_name?: string } }).reopen_by?.full_name}
                        roles={(additionalInfo as { reopen_by: { roles?: UserRole[] } }).reopen_by?.roles}
                      />
                    </div>
                    <div className="info-block">
                      <Typography.Label>Last reopen requested at</Typography.Label>
                      <Typography.Text>{dateFormatter(String(additionalInfo.reopen_at))}</Typography.Text>
                    </div>
                    <div className="info-block">
                      <Typography.Label>Reopen comment</Typography.Label>
                      <RichText text={String((additionalInfo as { reopen_comment?: string }).reopen_comment || '-')} />
                    </div>
                  </div>
                )}
                {additionalInfo.declined_at && (
                  <div className="opening-pause-info__item">
                    <div className="info-block">
                      <Typography.Label>Declined to reopen by</Typography.Label>
                      <User
                        className="opening-default__extra-user"
                        avatar={(additionalInfo as { declined_by: { avatar?: string } }).declined_by?.avatar}
                        fullName={(additionalInfo as { declined_by: { full_name?: string } }).declined_by?.full_name}
                        roles={(additionalInfo as { declined_by: { roles?: UserRole[] } }).declined_by?.roles}
                      />
                    </div>
                    <div className="info-block">
                      <Typography.Label>Declined to reopen at</Typography.Label>
                      <Typography.Text>{dateFormatter(String(additionalInfo.declined_at))}</Typography.Text>
                    </div>
                    <div className="info-block">
                      <Typography.Label>Decline comment</Typography.Label>
                      <RichText
                        text={String((additionalInfo as { decline_comment?: string }).decline_comment || '-')}
                      />
                    </div>
                  </div>
                )}
                {(additionalInfo.rm_approval_date || additionalInfo.lm_approval_date) && (
                  <div className="opening-pause-info__item">
                    {!!additionalInfo.rm_approval_date && (
                      <div className="info-block">
                        <Typography.Label>Last reopen RM approval date</Typography.Label>
                        <Typography.Text>{dateFormatter(String(additionalInfo.rm_approval_date))}</Typography.Text>
                      </div>
                    )}
                    {!!additionalInfo.lm_approval_date && (
                      <div className="info-block">
                        <Typography.Label>Last reopen LM approval date</Typography.Label>
                        <Typography.Text>{dateFormatter(String(additionalInfo.lm_approval_date))}</Typography.Text>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <Button type={BUTTON_TYPES.TEXT} className="opening-default__show-more" onClick={onShowMore}>
        {showMore ? 'Show less details' : 'Show more details'}
      </Button>
      <PopupPauseOpening
        open={openConfirmPause}
        opening={opening as OpeningData}
        jobName={jobName}
        onClose={onCloseConfirmPause}
        onSubmit={onPause}
      />
      <PopupReopenOpening
        open={openConfirmReopen}
        opening={opening as OpeningData}
        jobName={jobName}
        onClose={onCloseConfirmReopen}
        onSubmit={onReopen}
      />
    </Card>
  );
};
